/**
 * Static UI strings for the admin and dashboard pages (headings, column
 * names, buttons, hints). The public funnel is intentionally NOT covered:
 * its texts come from the funnel JSON and belong to the config author.
 */
export const en = {
  language: 'Language',
  common: {
    refresh: 'Refresh',
    close: 'Close',
    all: 'all',
    noData: 'no data',
  },
  admin: {
    title: 'Funnel versions',
    subtitle: 'Publish a draft to switch new sessions instantly. Running sessions stay on the version they started with.',
    token: 'Admin token',
    tokenPlaceholder: 'not required unless ADMIN_TOKEN is set',
    activeVersion: 'Active version',
    versionsStored: 'Versions stored',
    sessionsTotal: 'Sessions total',
    lastChange: 'Last change',
    colVersion: 'Version',
    colTitle: 'Title / experiment',
    colStatus: 'Status',
    colSteps: 'Steps',
    colSessions: 'Sessions',
    active: 'active',
    status: { published: 'published', draft: 'draft' } as Record<string, string>,
    created: (d: string) => `Created ${d}`,
    json: 'JSON',
    publish: 'Publish',
    noVersions: 'No versions yet. Upload one below.',
    rollBack: 'Roll back',
    rollBackTo: (v: string) => `Roll back to v${v}`,
    noRollback: 'No previous publication to roll back to',
    publishHint: 'Publishing switches new sessions immediately. Existing sessions stay pinned to the version they started on.',
    uploadTitle: 'Upload a version',
    uploadHint: 'Paste a funnel JSON or pick a file. It is validated against the schema and stored as a draft until you publish it.',
    uploadButton: 'Upload as draft',
    logTitle: 'Publication log',
    logEmpty: 'Nothing published yet.',
    action: { publish: 'publish', rollback: 'rollback' } as Record<string, string>,
    configOf: (v: string) => `v${v} — config`,
    notJson: 'Not valid JSON',
    noticeUploaded: 'Version uploaded as draft',
    noticePublished: (v: string) => `Published v${v}`,
    noticeRolledBack: (v: string) => `Rolled back to v${v}`,
  },
  dashboard: {
    title: 'Analytics',
    subtitle: 'Every number is a count of unique sessions — repeat views, back navigation and duplicate events never inflate it.',
    version: 'Version',
    variant: 'Variant',
    campaign: 'UTM campaign',
    kpiCtaPerStarted: 'CTA / started',
    kpiPrimary: 'primary A/B metric',
    kpiStarted: 'Sessions started',
    kpiReached: 'Reached result',
    kpiOfStarted: (p: string) => `${p} of started`,
    kpiCta: 'CTA clicked',
    kpiCtr: (p: string) => `${p} CTR of result viewers`,
    kpiEvents: 'Events stored',
    kpiDuplicates: (n: number) => `${n} duplicates ignored`,
    compareBy: 'Compare by',
    abTab: 'A vs B',
    abHint: 'Primary metric: share of started sessions that clicked the CTA.',
    versionsTab: 'Versions',
    versionsHint: 'Sessions are pinned to their version, so each keeps its own numbers after publish/rollback.',
    colStarted: 'Started',
    colResult: 'Result',
    colCompletion: 'Completion',
    colCta: 'CTA',
    colCtaCtr: 'CTA CTR',
    colCtaPerStarted: 'CTA / started',
    stepsTitle: 'Step funnel by version × variant',
    stepsHint:
      'Step order differs between variants (and some steps exist only in some versions), so the funnel is shown per segment. Drop-off is attributed to the furthest step a session saw without reaching the result.',
    emptyBefore: 'No events yet. Run',
    emptyAfter: 'or walk through the funnel.',
    segVariant: (v: string) => `variant ${v}`,
    segStarted: 'started',
    segReached: 'reached result',
    segCta: 'CTA',
    ctr: 'CTR',
    colStep: 'Step',
    colType: 'Type',
    colViewed: 'Viewed',
    colReach: 'Reach',
    colConv: 'Conv. from prev',
    colCompleted: 'Completed',
    colDropped: 'Dropped here',
    colDropRate: 'Drop rate',
    unknownSteps: (ids: string) => `Events for steps not in this segment's config: ${ids}`,
  },
};

export type Messages = typeof en;

export const ru: Messages = {
  language: 'Язык',
  common: {
    refresh: 'Обновить',
    close: 'Закрыть',
    all: 'все',
    noData: 'нет данных',
  },
  admin: {
    title: 'Версии воронки',
    subtitle: 'Публикация черновика мгновенно переключает новые сессии. Начатые сессии остаются на своей версии.',
    token: 'Админ-токен',
    tokenPlaceholder: 'не нужен, если ADMIN_TOKEN не задан',
    activeVersion: 'Активная версия',
    versionsStored: 'Версий в базе',
    sessionsTotal: 'Сессий всего',
    lastChange: 'Последнее изменение',
    colVersion: 'Версия',
    colTitle: 'Название / эксперимент',
    colStatus: 'Статус',
    colSteps: 'Шагов',
    colSessions: 'Сессий',
    active: 'активна',
    status: { published: 'опубликована', draft: 'черновик' },
    created: (d) => `Создана ${d}`,
    json: 'JSON',
    publish: 'Опубликовать',
    noVersions: 'Версий пока нет. Загрузите первую ниже.',
    rollBack: 'Откатить',
    rollBackTo: (v) => `Откатить на v${v}`,
    noRollback: 'Нет предыдущей публикации для отката',
    publishHint: 'Публикация сразу переключает новые сессии. Существующие сессии остаются на версии, с которой начали.',
    uploadTitle: 'Загрузить версию',
    uploadHint: 'Вставьте JSON воронки или выберите файл. Он проверяется по схеме и хранится как черновик до публикации.',
    uploadButton: 'Загрузить как черновик',
    logTitle: 'Журнал публикаций',
    logEmpty: 'Публикаций ещё не было.',
    action: { publish: 'публикация', rollback: 'откат' },
    configOf: (v) => `v${v} — конфиг`,
    notJson: 'Некорректный JSON',
    noticeUploaded: 'Версия загружена как черновик',
    noticePublished: (v) => `Опубликована v${v}`,
    noticeRolledBack: (v) => `Откат на v${v} выполнен`,
  },
  dashboard: {
    title: 'Аналитика',
    subtitle: 'Все числа — количество уникальных сессий: повторные просмотры, возвраты назад и дубли событий их не раздувают.',
    version: 'Версия',
    variant: 'Вариант',
    campaign: 'UTM-кампания',
    kpiCtaPerStarted: 'CTA / начали',
    kpiPrimary: 'основная метрика A/B',
    kpiStarted: 'Начали воронку',
    kpiReached: 'Дошли до результата',
    kpiOfStarted: (p) => `${p} от начавших`,
    kpiCta: 'Нажали CTA',
    kpiCtr: (p) => `CTR ${p} от увидевших результат`,
    kpiEvents: 'Событий сохранено',
    kpiDuplicates: (n) => `${n} дублей отброшено`,
    compareBy: 'Сравнить по',
    abTab: 'A против B',
    abHint: 'Основная метрика: доля начавших сессий, где нажали CTA.',
    versionsTab: 'Версии',
    versionsHint: 'Сессии закреплены за своей версией, поэтому у каждой версии свои цифры и после публикации/отката.',
    colStarted: 'Начали',
    colResult: 'Результат',
    colCompletion: 'Доходимость',
    colCta: 'CTA',
    colCtaCtr: 'CTR CTA',
    colCtaPerStarted: 'CTA / начали',
    stepsTitle: 'Воронка по шагам: версия × вариант',
    stepsHint:
      'Порядок шагов у вариантов разный (а некоторые шаги есть не во всех версиях), поэтому воронка показана по сегментам. Отвал приписывается самому дальнему шагу, который сессия увидела, не дойдя до результата.',
    emptyBefore: 'Событий пока нет. Запустите',
    emptyAfter: 'или пройдите воронку.',
    segVariant: (v) => `вариант ${v}`,
    segStarted: 'начали',
    segReached: 'дошли до результата',
    segCta: 'CTA',
    ctr: 'CTR',
    colStep: 'Шаг',
    colType: 'Тип',
    colViewed: 'Просмотры',
    colReach: 'Дошли',
    colConv: 'Конверсия с пред.',
    colCompleted: 'Завершили',
    colDropped: 'Отвал здесь',
    colDropRate: 'Доля отвала',
    unknownSteps: (ids) => `События по шагам, которых нет в конфиге сегмента: ${ids}`,
  },
};

export const locales = { en, ru } as const;
export type Locale = keyof typeof locales;
